import { Column, ColumnDef, Row, SortDirection } from "@tanstack/react-table";
import {
  ModelScreenerSpecificColumnSizes,
  MSCellDataConstants,
  MSColumnHelper,
  MSDefaultDateFormat,
  MSDefaultNumericDecimalPlaces,
  MSOverScanConstants,
  MSSubHeaderIdConstants,
  MSTableDefaultSizeConstants,
  MSTableStickyConstants,
} from "@pages/managed-accounts/model-screener/constant";
import { SHStack, SHTypography } from "@components/design-systems";
import { isNil, round } from "lodash";
import { MSTableSelectedRowCheckbox } from "src/pages/managed-accounts/model-screener/components/model-screener-table/checkboxes/row-select";
import { MSTableFavoritedSelect } from "@pages/managed-accounts/model-screener/components/model-screener-table/checkboxes/favorited-select";
import { MSTableCellLayout } from "@pages/managed-accounts/model-screener/components/model-screener-table/rendered-fields/cell-layout";
import { ModelScreenerFieldType } from "@models/model-screener/enums/fieldType";
import {
  ModelScreenerData,
  ModelScreenerRow,
  ModelScreenerSubHeader,
} from "@models/model-screener/entities/modelScreenerData";
import { isValid, parse } from "date-fns";
import { MSTableRestrictedCell } from "@pages/managed-accounts/model-screener/components/model-screener-table/rendered-fields/restricted-cell";
import { ModelScreenerFilterType } from "@models/model-screener/enums/filterType";

export const renderModelScreenerColumnGroups = ({
  data,
  subHeaderFlattenedIndexes,
  columnVisibility,
}: {
  data?: ModelScreenerData;
  subHeaderFlattenedIndexes: Record<string, number>;
  columnVisibility: Record<string, boolean>;
}): ColumnDef<ModelScreenerRow, any>[] => {
  if (!data || !subHeaderFlattenedIndexes) return [];

  let groupsResult: ColumnDef<ModelScreenerRow>[] = [];

  const staticGroups = renderModelScreenerStaticColumnGroups();
  groupsResult.push(staticGroups);

  data.headers?.forEach((header, index) => {
    const group = MSColumnHelper.group({
      id: header.id,
      header: header.name,
      columns: renderModelScreenerSubHeaders({
        subHeaders: header.subHeaders,
        parentIndex: index,
        subHeaderFlattenedIndexes,
        columnVisibility,
      }),
      meta: {
        isLastColumn: true,
      },
    });

    groupsResult.push(group);
  });

  return groupsResult;
};

export const renderModelScreenerStaticColumnGroups = () => {
  return MSColumnHelper.group({
    id: "selectCol",
    header: (info) => (
      <SHTypography
        variant="body1"
        colorVariant="third"
        lineHeight={1.2}
        fontSize={11}
      >
        Showing {info.table.getRowModel().rows.length}
      </SHTypography>
    ),
    columns: [
      MSColumnHelper.accessor((row) => row, {
        id: "select",
        header: () => "Select",
        cell: (info) => {
          const rowId = info.row.original.id;

          return (
            <SHStack
              flexDirection={"row"}
              alignItems={"center"}
              marginX={"-10px"}
            >
              <MSTableSelectedRowCheckbox rowId={rowId} />
              <MSTableFavoritedSelect rowId={rowId} />
            </SHStack>
          );
        },
        size: 90,
        meta: {
          isSelectColumn: true,
        },
        enableSorting: false,
        enableColumnFilter: false,
      }),
    ],
  });
};

export const renderModelScreenerSubHeaders = ({
  subHeaders,
  parentIndex,
  subHeaderFlattenedIndexes,
  columnVisibility,
}: {
  subHeaders: ModelScreenerSubHeader[];
  parentIndex: number;
  subHeaderFlattenedIndexes: Record<string, number>;
  columnVisibility: Record<string, boolean>;
}): ColumnDef<ModelScreenerRow, any>[] => {
  let lastVisibleIndex = -1;
  const renderedSubHeaders = subHeaders.map((subHeader, index) => {
    const filterEnabled = isFilterEnabled(subHeader.filterType);
    const isVisible = columnVisibility[subHeader.id];
    if (isVisible) lastVisibleIndex = index;

    return MSColumnHelper.accessor((row) => row, {
      id: subHeader.id,
      header: subHeader.name,
      enableSorting: subHeader.sortable,
      sortingFn: (rowA, rowB, columnId) =>
        getSortingFunction({
          rowA,
          rowB,
          columnId,
          subHeader,
          subHeaderFlattenedIndexes,
        }),
      cell: (info) => {
        const cellValue =
          info.row.original.data?.[subHeaderFlattenedIndexes[subHeader.id]];

        const isDefaultStickyCells =
          parentIndex === 0 &&
          index <= MSTableStickyConstants.LastBodyColumnIndex &&
          subHeader.fieldTypeId !== ModelScreenerFieldType.Manager;

        if (!cellValue) return <></>;

        if (!!cellValue.isRestricted) {
          return <MSTableRestrictedCell />;
        }

        return (
          <MSTableCellLayout
            key={`${info.row.index}-${index}`}
            cellData={cellValue}
            fieldType={subHeader.fieldTypeId}
            isDefaultStickyCells={isDefaultStickyCells}
          />
        );
      },
      enableColumnFilter: filterEnabled,
      filterFn: (row, columnId, filterValue) => {
        if (!filterEnabled) return true;

        return getFilterFunction({
          subHeader,
          row,
          columnId,
          filterValue,
          subHeaderFlattenedIndexes,
        });
      },
      size: getColumnWidth({
        originalSize: subHeader.size,
        filterEnabled,
        subHeaderName: subHeader.name,
      }),
      meta: {
        tooltip: !!subHeader.fullName
          ? {
              title: subHeader.fullName,
              description: subHeader.description,
            }
          : null,
        isLastColumn: false,
        filterData: subHeader.filterData || [],
        fieldType: subHeader.fieldTypeId,
        filterType: subHeader.filterType,
      },
    });
  });

  if (lastVisibleIndex >= 0) {
    renderedSubHeaders[lastVisibleIndex] = {
      ...renderedSubHeaders[lastVisibleIndex],
      meta: {
        ...renderedSubHeaders[lastVisibleIndex].meta,
        isLastColumn: true,
      },
    };
  }

  return renderedSubHeaders;
};

const modelScreenerGeneralSortFn = ({
  valueA,
  valueB,
}: {
  valueA: string;
  valueB: string;
}) => {
  return valueA?.localeCompare(valueB);
};

const modelScreenerYesNoSortFn = ({
  valueA,
  valueB,
}: {
  valueA: string;
  valueB: string;
}) => {
  const aIsTrue = valueA === MSCellDataConstants.Yes;
  const bIsTrue = valueB === MSCellDataConstants.Yes;

  return aIsTrue === bIsTrue ? 0 : aIsTrue ? -1 : 1;
};

const modelScreenerNumberSortFn = ({
  valueA,
  valueB,
}: {
  valueA: string;
  valueB: string;
}) => {
  if (!isNaN(Number(valueA)) && !isNaN(Number(valueB))) {
    return Number(valueA) - Number(valueB);
  }

  return modelScreenerGeneralSortFn({ valueA, valueB });
};

const modelScreenerDateTimeSortFn = ({
  valueA,
  valueB,
}: {
  valueA: string;
  valueB: string;
}) => {
  const dateA = parse(valueA, MSDefaultDateFormat, new Date());
  const dateB = parse(valueB, MSDefaultDateFormat, new Date());

  if (isValid(dateA) && isValid(dateB)) {
    return dateA.getTime() - dateB.getTime();
  }

  return modelScreenerGeneralSortFn({ valueA, valueB });
};

const MSTableSortFunctionMap: Record<
  ModelScreenerFieldType,
  ({ valueA, valueB }: { valueA: string; valueB: string }) => number
> = {
  [ModelScreenerFieldType.Dropdown]: modelScreenerGeneralSortFn,
  [ModelScreenerFieldType.TextShort]: modelScreenerGeneralSortFn,
  [ModelScreenerFieldType.Manager]: modelScreenerGeneralSortFn,
  [ModelScreenerFieldType.YesNo]: modelScreenerYesNoSortFn,
  [ModelScreenerFieldType.Currency]: modelScreenerNumberSortFn,
  [ModelScreenerFieldType.CurrencyText]: modelScreenerNumberSortFn,
  [ModelScreenerFieldType.Percentage]: modelScreenerNumberSortFn,
  [ModelScreenerFieldType.PercentageText]: modelScreenerNumberSortFn,
  [ModelScreenerFieldType.Number]: modelScreenerNumberSortFn,
  [ModelScreenerFieldType.NumberText]: modelScreenerNumberSortFn,
  [ModelScreenerFieldType.DateTime]: modelScreenerDateTimeSortFn,
  [ModelScreenerFieldType.TextLong]: () => 0,
  [ModelScreenerFieldType.Link]: () => 0,
  [ModelScreenerFieldType.Image]: () => 0,
};

const getSortingFunction = ({
  rowA,
  rowB,
  columnId,
  subHeader,
  subHeaderFlattenedIndexes,
}: {
  rowA: Row<ModelScreenerRow>;
  rowB: Row<ModelScreenerRow>;
  columnId: string;
  subHeader: ModelScreenerSubHeader;
  subHeaderFlattenedIndexes: Record<string, number>;
}): number => {
  const { sortable, fieldTypeId } = subHeader;

  if (!sortable) return 0;

  const { value: valueA, isRestricted: isARestricted } =
    rowA.original.data?.[subHeaderFlattenedIndexes[subHeader.id]] ?? {};
  const { value: valueB, isRestricted: isBRestricted } =
    rowB.original.data?.[subHeaderFlattenedIndexes[subHeader.id]] ?? {};

  if ((!valueA || !valueB) && !isARestricted && !isBRestricted) return 0;

  const cellsByColumnId = rowA._getAllCellsByColumnId();
  const sortDirection = cellsByColumnId[columnId].column.getIsSorted();

  const sortSpecialValuesResult = sortSpecialValues({
    valueA,
    valueB,
    sortDirection,
  });

  if (!isNil(sortSpecialValuesResult)) return sortSpecialValuesResult;

  const sortingFunction = MSTableSortFunctionMap[fieldTypeId] || (() => 0);
  return sortingFunction({ valueA, valueB });
};

const sortSpecialValues = ({
  valueA,
  valueB,
  sortDirection,
}: {
  valueA?: string | null;
  valueB?: string | null;
  sortDirection?: false | SortDirection;
}): number | null => {
  const sortMultiplier = sortDirection === "desc" ? -1 : 1;

  // Handle "No Data" cells
  if (valueA === MSCellDataConstants.NoData) {
    return sortMultiplier;
  }
  if (valueB === MSCellDataConstants.NoData) {
    return -sortMultiplier;
  }

  // Handle "N/A" cells
  if (valueA === MSCellDataConstants.NAData) {
    return sortMultiplier;
  }
  if (valueB === MSCellDataConstants.NAData) {
    return -sortMultiplier;
  }

  // Handle "Restricted" cells
  if (valueA === MSCellDataConstants.HiddenData || isNil(valueA)) {
    return sortMultiplier;
  }
  if (valueB === MSCellDataConstants.HiddenData || isNil(valueB)) {
    return -sortMultiplier;
  }

  return null;
};

const isFilterEnabled = (filterType?: ModelScreenerFilterType | null) => {
  if (!filterType) return false;
  return [
    ModelScreenerFilterType.Checkbox,
    ModelScreenerFilterType.Column,
  ].includes(filterType);
};

const filterCheckbox = ({
  row,
  columnId,
  filterValue,
  subHeaderFlattenedIndexes,
}: {
  row: Row<ModelScreenerRow>;
  columnId: string;
  filterValue: any;
  subHeaderFlattenedIndexes: Record<string, number>;
}) => {
  if (!Array.isArray(filterValue)) return true;

  const targetData = row.original.data?.[subHeaderFlattenedIndexes[columnId]];

  return filterValue?.includes(targetData?.value);
};

const MSTableFilterFunctionMap: Record<
  ModelScreenerFilterType,
  ({
    row,
    columnId,
    filterValue,
    subHeaderFlattenedIndexes,
  }: {
    row: Row<ModelScreenerRow>;
    columnId: string;
    filterValue: any;
    subHeaderFlattenedIndexes: Record<string, number>;
  }) => boolean
> = {
  [ModelScreenerFilterType.Checkbox]: filterCheckbox,
  [ModelScreenerFilterType.Column]: filterCheckbox,
  [ModelScreenerFilterType.ViaSearch]: () => true,
  [ModelScreenerFilterType.TrackRecord]: () => true,
  [ModelScreenerFilterType.Range]: () => true,
  [ModelScreenerFilterType.ViaMainFilter]: () => true,
  [ModelScreenerFilterType.Custom]: () => true,
  [ModelScreenerFilterType.None]: () => true,
};

const getFilterFunction = ({
  subHeader,
  row,
  columnId,
  filterValue,
  subHeaderFlattenedIndexes,
}: {
  subHeader: ModelScreenerSubHeader;
  row: Row<ModelScreenerRow>;
  columnId: string;
  filterValue: any;
  subHeaderFlattenedIndexes: Record<string, number>;
}): boolean => {
  const filterType = subHeader.filterType as ModelScreenerFilterType;

  const filterFunction = MSTableFilterFunctionMap[filterType] ?? (() => true);
  return filterFunction({
    row,
    columnId,
    filterValue,
    subHeaderFlattenedIndexes,
  });
};

const getColumnWidth = ({
  originalSize,
  filterEnabled,
  subHeaderName,
}: {
  originalSize?: number;
  filterEnabled: boolean;
  subHeaderName?: string;
}): number => {
  if (subHeaderName) {
    for (const key of Object.keys(ModelScreenerSpecificColumnSizes)) {
      if (subHeaderName === key || subHeaderName.includes(key)) {
        return ModelScreenerSpecificColumnSizes[key];
      }
    }
  }

  if (!originalSize) return MSTableDefaultSizeConstants.ColumnWidth;

  return (
    originalSize +
    (filterEnabled ? MSTableDefaultSizeConstants.FilterIconWidth : 0)
  );
};

export const getModelScreenerNumericCellValue = ({
  cellValue,
  prefix = "",
  suffix = "",
}: {
  cellValue: string;
  prefix?: string;
  suffix?: string;
}): string => {
  if (
    [MSCellDataConstants.NAData, MSCellDataConstants.NoData].includes(cellValue)
  ) {
    return cellValue;
  }

  const floatValue = parseFloat(cellValue);
  const isValidNumber = !isNaN(floatValue);

  if (!isValidNumber) return cellValue;

  const roundedValue = round(floatValue, MSDefaultNumericDecimalPlaces).toFixed(
    MSDefaultNumericDecimalPlaces,
  );

  if (!!suffix) {
    return `${roundedValue}${suffix}`;
  } else {
    return `${prefix}${roundedValue}`;
  }
};

export const getMSTableVirtualizerSetup = ({
  columns,
}: {
  columns: Column<ModelScreenerRow, any>[];
}) => {
  const isNotFireFox =
    typeof window !== "undefined" &&
    navigator.userAgent.indexOf("Firefox") === -1;

  const rowOverscan = isNotFireFox
    ? MSOverScanConstants.DefaultRowOverscan
    : MSOverScanConstants.FireFoxRowOverscan;
  const columnOverscan = isNotFireFox
    ? MSOverScanConstants.DefaultColumnOverscan
    : MSOverScanConstants.FireFoxColumnOverscan;

  const measureElementHeight = isNotFireFox
    ? (element: Element) => element?.getBoundingClientRect().height
    : undefined;
  const measureElementWidth = isNotFireFox
    ? (element: Element) => element?.getBoundingClientRect().width
    : undefined;

  const rowEstimateSizeFunction = () => MSTableDefaultSizeConstants.CellHeight;
  const columnEstimateSizeFunction = (index: number): number =>
    columns[index]?.getSize() ?? MSTableDefaultSizeConstants.ColumnWidth;

  return {
    rowEstimateSizeFunction,
    columnEstimateSizeFunction,
    rowOverscan,
    columnOverscan,
    measureElementHeight,
    measureElementWidth,
  };
};

export const getPreComputeModelScreenerData = (
  msData: ModelScreenerData,
): {
  viaSearchSubHeaderIds: Record<string, boolean>;
  subHeaderFlattenedIndexes: Record<string, number>;
  columnVisibility: Record<string, boolean>;
  favouritedRowIds: Record<string, boolean>;
  combinedSearchValues: Record<string, string[]>;
} => {
  const viaSearchSubHeaderIds: Record<string, boolean> = {};
  const subHeaderFlattenedIndexes: Record<string, number> = {};
  const columnVisibility: Record<string, boolean> = {};
  const favouritedRowIds: Record<string, boolean> = {};
  const combinedSearchValues: Record<string, string[]> = {};
  let modelManagerSubheaderIndex = 0;
  let modelSeriesSubheaderIndex = 0;
  let modelModelSubheaderIndex = 0;

  msData.headers
    ?.flatMap((header) => header.subHeaders)
    ?.forEach((s, i) => {
      if (s.filterType === ModelScreenerFilterType.ViaSearch)
        viaSearchSubHeaderIds[s.id] = true;

      subHeaderFlattenedIndexes[s.id] = i;
      columnVisibility[s.id] = true;

      if (s.id === MSSubHeaderIdConstants.ModelManager)
        modelManagerSubheaderIndex = i;
      if (s.id === MSSubHeaderIdConstants.ModelSeries)
        modelSeriesSubheaderIndex = i;
      if (s.id === MSSubHeaderIdConstants.ModelModel)
        modelModelSubheaderIndex = i;
    });

  msData.data?.forEach((row) => {
    if (row.isFavourited) {
      favouritedRowIds[row.id] = true;
    }

    const rowData = row.data;

    combinedSearchValues[row.id] = [
      rowData[modelManagerSubheaderIndex].value,
      rowData[modelSeriesSubheaderIndex].value,
      rowData[modelModelSubheaderIndex].value,
    ].flatMap((value) => value.toLowerCase().split(" "));
  });

  return {
    viaSearchSubHeaderIds,
    subHeaderFlattenedIndexes,
    columnVisibility,
    favouritedRowIds,
    combinedSearchValues,
  };
};
